// Abrir chamado a partir da conversa.
//
// O assunto é o único campo obrigatório, e é de propósito: o chamado nasce no
// meio de um atendimento, com o cliente esperando do outro lado. Formulário
// longo aqui vira chamado que ninguém abre — e aí o protocolo não existe
// justamente nos casos que mais precisariam dele.
//
// O protocolo NÃO é escolhido aqui: quem gera é o banco, na hora de gravar.

import React, { useState } from 'react';
import { AlertCircle, Loader2, TicketPlus, X } from 'lucide-react';
import type { TicketPrioridade } from '../tipos';

interface Props {
  nomeContato: string;
  salvando: boolean;
  erro: string | null;
  onFechar: () => void;
  onAbrir: (dados: { assunto: string; prioridade: TicketPrioridade; descricao: string }) => void;
}

const PRIORIDADES: { valor: TicketPrioridade; rotulo: string }[] = [
  { valor: 'baixa', rotulo: 'Baixa' },
  { valor: 'normal', rotulo: 'Normal' },
  { valor: 'alta', rotulo: 'Alta' },
  { valor: 'urgente', rotulo: 'Urgente' },
];

export default function ModalAbrirChamado({ nomeContato, salvando, erro, onFechar, onAbrir }: Props) {
  const [assunto, setAssunto] = useState('');
  const [prioridade, setPrioridade] = useState<TicketPrioridade>('normal');
  const [descricao, setDescricao] = useState('');

  const podeAbrir = assunto.trim().length > 2 && !salvando;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-slate-800">
        <header className="flex items-center justify-between border-b border-gray-200 px-5 py-3.5 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <TicketPlus size={16} className="text-primary" />
            <div>
              <h2 className="text-sm font-bold text-gray-900 dark:text-white">Abrir chamado</h2>
              <p className="text-xs text-gray-500 dark:text-slate-400">{nomeContato}</p>
            </div>
          </div>
          <button
            onClick={onFechar}
            className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-slate-700"
            aria-label="Fechar"
          >
            <X size={16} />
          </button>
        </header>

        <div className="px-5 py-4">
          {erro && (
            <div className="mb-3 flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-900/20 dark:text-red-300">
              <AlertCircle size={13} className="mt-0.5 shrink-0" />
              <span>{erro}</span>
            </div>
          )}

          <label className="block text-xs font-medium text-gray-600 dark:text-slate-300">
            Assunto
            <input
              value={assunto}
              onChange={(e) => setAssunto(e.target.value)}
              autoFocus
              placeholder="Boleto de setembro não chegou"
              className="mt-1 w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-normal text-gray-800 placeholder:text-gray-400 focus:border-primary focus:bg-white focus:outline-none dark:border-slate-600 dark:bg-slate-900/60 dark:text-slate-100"
            />
          </label>

          <div className="mt-3">
            <span className="text-xs font-medium text-gray-600 dark:text-slate-300">Prioridade</span>
            <div className="mt-1 flex gap-1.5">
              {PRIORIDADES.map((p) => (
                <button
                  key={p.valor}
                  onClick={() => setPrioridade(p.valor)}
                  className={`flex-1 rounded-xl px-2 py-1.5 text-xs font-medium transition-colors ${
                    prioridade === p.valor
                      ? 'bg-primary text-white'
                      : 'border border-gray-200 text-gray-600 hover:border-primary/40 dark:border-slate-600 dark:text-slate-300'
                  }`}
                >
                  {p.rotulo}
                </button>
              ))}
            </div>
          </div>

          <label className="mt-3 block text-xs font-medium text-gray-600 dark:text-slate-300">
            Detalhe (opcional)
            <textarea
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              rows={3}
              placeholder="O que o cliente pediu, o que já foi verificado…"
              className="mt-1 w-full resize-none rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-normal text-gray-800 placeholder:text-gray-400 focus:border-primary focus:bg-white focus:outline-none dark:border-slate-600 dark:bg-slate-900/60 dark:text-slate-100"
            />
          </label>

          <p className="mt-2.5 text-[11px] text-gray-400 dark:text-slate-500">
            O número do protocolo é gerado na hora de salvar e serve para o cliente acompanhar, mesmo
            que a conversa continue por outro canal.
          </p>

          <div className="mt-4 flex justify-end gap-2">
            <button
              onClick={onFechar}
              className="rounded-xl px-3 py-2 text-xs font-medium text-gray-500 hover:text-gray-700 dark:text-slate-400"
            >
              Cancelar
            </button>
            <button
              onClick={() => onAbrir({ assunto: assunto.trim(), prioridade, descricao: descricao.trim() })}
              disabled={!podeAbrir}
              className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-primary/90 disabled:bg-gray-200 disabled:text-gray-400 dark:disabled:bg-slate-700"
            >
              {salvando && <Loader2 size={12} className="animate-spin" />}
              Abrir chamado
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
